import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import type { ITeacherTerm } from '@/entities/TeacherTerms';
import { Svg } from '@/shared/ui/Svg';
import EditIcon from '@/shared/assets/icons/edit.svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';

interface TermsColumnsProps {
  onRowClick?: (term: ITeacherTerm) => void;
  onEdit?: (term: ITeacherTerm) => void;
  readOnly?: boolean;
}

export const termsColumns = ({
  onRowClick,
  onEdit,
  readOnly = false,
}: TermsColumnsProps = {}): ColumnDef<ITeacherTerm>[] => [
    {
      header: 'Terms',
      accessorKey: 'number',
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => onRowClick?.(row.original)}
          className="text-black-headline font-medium hover:underline text-left cursor-pointer"
        >
          Term {row.original.number}
        </button>
      ),
    },
    {
      header: 'Students on the course',
      accessorKey: 'studentCount',
      cell: ({ row }) => (
        <span className="text-black-headline">{row.original.studentCount}</span>
      ),
    },
    {
      header: 'Modules',
      accessorKey: 'modulesCount',
      cell: ({ row }) => (
        <span className="text-black-headline">{row.original.modulesCount}</span>
      ),
    },
    {
      header: 'Duration',
      accessorKey: 'totalLessonsDuration',
      cell: ({ row }) => {
        const total = row.original.totalLessonsDuration ?? 0;
        const hours = Math.floor(total / 60);
        const minutes = total % 60;
        return (
          <span className="text-black-headline">
            {hours > 0 ? `${hours} h ` : ''}
            {minutes > 0 ? `${minutes} min` : hours === 0 && minutes === 0 ? '—' : ''}
          </span>
        );
      },
    },
    {
      header: () => (
        <div className="flex items-center gap-2">
          <span>Start Date</span>
          <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
        </div>
      ),
      accessorKey: 'startDate',
      cell: ({ row }) => {
        const dateStr = row.original.startDate;
        if (!dateStr) return <span className="text-grey-secondary">—</span>;
        try {
          const date = new Date(dateStr);
          return <span className="text-black-headline">{format(date, 'dd.MM.yyyy')}</span>;
        } catch {
          return <span className="text-grey-secondary">—</span>;
        }
      },
    },
    {
      header: () => (
        <div className="flex items-center gap-2">
          <span>End Date</span>
          <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
        </div>
      ),
      accessorKey: 'endDate',
      cell: ({ row }) => {
        const dateStr = row.original.endDate;
        if (!dateStr) return <span className="text-grey-secondary">—</span>;
        try {
          const date = new Date(dateStr);
          return <span className="text-black-headline">{format(date, 'dd.MM.yyyy')}</span>;
        } catch {
          return <span className="text-grey-secondary">—</span>;
        }
      },
    },
    ...(readOnly
      ? []
      : [
        {
          id: 'actions',
          header: '',
          cell: ({ row }: { row: { original: ITeacherTerm } }) => (
            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(row.original);
                }}
                className="p-2 hover:bg-grey-10 rounded-lg transition-colors text-grey-secondary"
                aria-label="Edit term"
              >
                <Svg Icon={EditIcon} width={20} height={20} />
              </button>
            </div>
          ),
        } as ColumnDef<ITeacherTerm>,
      ]),
  ];
